import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from '../../layout/DefaultLayout';
import Loading from '../../components/Loading';
import Alert from '../../components/Alert';
import { useGlobalState } from '../../state';
import { FaBell } from 'react-icons/fa';
import notificationService from '../../services/notificationService';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';

type NotificationDto = {
  idNotificacion?: number;
  titulo?: string;
  mensaje?: string;
  fecha?: string;
  notificacionVerificada?: boolean;
  redirectPath?: string;
};

const truncateText = (text: string = '', length: number) =>
  text.length > length ? `${text.substring(0, length)}...` : text;

const Notificaciones: React.FC = () => {
  const navigate = useNavigate();
  const [user] = useGlobalState('user');
  const [loading, setLoading] = useState<boolean>(false);
  const [showToken, setShowToken] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);
  const [notifications, setNotifications] = useState<NotificationDto[]>([]);
  const [expoPushToken, setExpoPushToken] = useState<string | undefined>(undefined);
  const [lastNotification, setLastNotification] = useState<any>(undefined);

  useEffect(() => {
    if (user === undefined) return; // still checking auth
    if (!user) {
      navigate('/auth/signin');
      return;
    }
    fetchNotifications(user.datosUsuario.legajoPrevisional);
    // try to read a stored push token if any
    try {
      const token = localStorage.getItem('expoPushToken');
      if (token) setExpoPushToken(token);
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchNotifications = async (idAfiliado: string) => {
    setLoading(true);
    setErrorText(undefined);
    try {
      const res: any = await notificationService.obtenerNotificaciones?.(idAfiliado);
      if (res?.success) {
        const list = res.notifications ?? res.data ?? res.data?.notifications ?? [];
        setNotifications(Array.isArray(list) ? list : []);
        // set last notification for debugging / display
        if (Array.isArray(list) && list.length > 0) {
          setLastNotification(list[0]);
        }
      } else {
        setErrorText(res?.errors?.[0] ?? 'No se pudieron cargar las notificaciones');
      }
    } catch (ex: any) {
      console.error('No se pudieron cargar las notificaciones:', ex);
      setErrorText(ex?.message ?? 'Ocurrió un error inesperado al cargar las notificaciones');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      if (navigator.clipboard && expoPushToken) {
        await navigator.clipboard.writeText(expoPushToken);
      }
    } catch (ex) {
      console.warn('Clipboard failed', ex);
    }
  };

  const displayToken = () => setShowToken((s) => !s);

  const handleNotificationPress = (notif: NotificationDto) => {
    const notificationData = {
      title: notif.titulo,
      message: notif.mensaje,
      date: notif.fecha ? new Date(notif.fecha).toLocaleString() : '',
      idnotificacion: notif.idNotificacion,
    };

    try {
      localStorage.setItem('notificationResponse', JSON.stringify(notificationData));
    } catch (ex) {
      console.error('Error saving notification to storage', ex);
    }

    const redirectPath = notif.redirectPath ?? '/notificaciones/detalle';
    if (notif.idNotificacion) {
      // include id param if available
      navigate(`${redirectPath}?id=${encodeURIComponent(String(notif.idNotificacion))}`);
    } else {
      navigate(redirectPath);
    }
  };

  return (
      <DefaultLayout>
          <Breadcrumb pageName="Notificaciones" />

      <div className="p-2">
        <div className="max-w-6xl mx-auto">
          
          {loading && <Loading statusText="Cargando notificaciones..." />}

          {errorText && <Alert type="failure" message={errorText} />}

          {!loading && notifications.length === 0 && (
            <div className="bg-white dark:bg-boxdark rounded p-4 mb-4">
              <button
                onClick={displayToken}
                className="text-left w-full"
                aria-label="Mostrar información"
              >
                <div className="text-sm text-gray-700">
                  Aquí encontrarás las novedades y avisos importantes sobre tu cuenta.
                </div>
              </button>
            </div>
          )}

          
          <div className="space-y-3">
            {loading ? null : notifications.map((notif, idx) => (
              <button
                key={idx}
                onClick={() => handleNotificationPress(notif)}
                className={`w-full text-left rounded-lg p-4 bg-white dark:bg-boxdark shadow-sm hover:shadow-md transition ${notif.notificacionVerificada === false ? 'border-l-4 border-primario-azul-900' : ''}`}
                aria-label={`Abrir notificación ${notif.titulo}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-full">
                    <FaBell className="text-2xl text-primario-celeste-800" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-800">{notif.titulo}</div>
                    <div className="text-xs text-gray-500 mt-1">{new Date(notif.fecha ?? '').toLocaleDateString()}</div>
                    <div className="text-sm text-gray-700 mt-2">{truncateText(notif.mensaje ?? '', 80)}</div>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </DefaultLayout>
  );
};

export default Notificaciones;